/**
 * Fills the product cards of blog posts (.amc-post .amc-card[data-product-handle]):
 * image, title, price, variant selector, add-to-cart button and link to the product page.
 * Shopify strips <script> tags from the article body, so the theme loads this file instead.
 */
(() => {
  /**
   * Standard storefront event 'shopify:cart:lines-update' (same shape as CartLinesUpdateEvent from
   * @shopify/events), so AI agents and the Shopify runtime know when lines are added.
   */
  class CartLinesUpdateEvent extends Event {
    constructor(lines, detail) {
      super('shopify:cart:lines-update', { bubbles: true, cancelable: true });
      this.action = 'add';
      this.context = 'product';
      this.lines = lines;
      this.detail = detail;
      this.promise = new Promise((resolve, reject) => {
        this.resolve = resolve;
        this.reject = reject;
      });
      this.promise.catch(() => {});
    }
  }

  const money = (cents) => {
    try {
      return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(cents / 100);
    } catch (e) {
      return `${(cents / 100).toFixed(2)} €`;
    }
  };

  const productLink = (card, text) => {
    const a = document.createElement('a');
    a.className = 'amc-product-link';
    a.href = card.link;
    a.textContent = text;
    return a;
  };

  const initCard = async (el, index) => {
    if (el.dataset.amcReady) return;
    el.dataset.amcReady = 'true';

    const handle = el.dataset.productHandle;
    const card = { link: `/products/${encodeURIComponent(handle)}` };
    const body = el.querySelector('.amc-card-inner');
    const status = el.querySelector('.amc-status');
    if (!body || !status) return;

    try {
      const resp = await fetch(`${card.link}.js`, { credentials: 'same-origin' });
      if (!resp.ok) throw new Error('Producto no disponible');
      const product = await resp.json();
      const variants = product.variants || [];
      if (!variants.length) throw new Error('Sin variantes');

      const img = product.featured_image || (product.images && product.images[0]);
      if (img) {
        const a = document.createElement('a');
        a.href = card.link;
        a.tabIndex = -1;
        a.style.display = 'block';
        a.style.lineHeight = '0';
        const im = document.createElement('img');
        im.style.display = 'block';
        im.style.margin = '0';
        im.src = img;
        im.alt = product.title;
        im.loading = 'lazy';
        a.appendChild(im);
        el.insertBefore(a, body);
      }

      const title = body.querySelector('.amc-card-title');
      if (title) {
        title.textContent = product.title;
        title.href = card.link;
      }

      const price = document.createElement('div');
      price.className = 'amc-price';

      const form = document.createElement('form');
      form.action = '/cart/add';
      form.method = 'post';
      form.style.display = 'contents';

      const selectId = `amc-variant-${index}-${product.id}`;
      const label = document.createElement('label');
      label.className = 'amc-spec';
      label.htmlFor = selectId;
      label.textContent = 'Formato de compra';

      const selector = document.createElement('select');
      selector.id = selectId;
      selector.name = 'id';
      variants.forEach((v) => {
        const opt = document.createElement('option');
        opt.value = v.id;
        opt.textContent = (v.public_title || v.title || 'Formato único') + (v.available ? '' : ' — Agotado');
        selector.appendChild(opt);
      });
      const firstAvailable = variants.find((v) => v.available);
      if (firstAvailable) selector.value = String(firstAvailable.id);

      const quantity = document.createElement('input');
      quantity.type = 'hidden';
      quantity.name = 'quantity';
      quantity.value = '1';

      const button = document.createElement('button');
      button.type = 'submit';
      button.className = 'amc-buy';

      const update = () => {
        const v = variants.find((x) => String(x.id) === selector.value) || variants[0];
        price.textContent = money(v.price);
        button.disabled = !v.available;
        button.textContent = v.available ? 'Añadir al carrito' : 'Agotado';
        status.textContent = v.available ? 'Disponible para comprar' : 'Consulta disponibilidad en la ficha';
      };

      selector.addEventListener('change', update);

      form.addEventListener('submit', async (evt) => {
        evt.preventDefault();
        const id = Number(selector.value);
        button.disabled = true;
        button.textContent = 'Añadiendo…';

        const cartLinesEvent = new CartLinesUpdateEvent([{
          merchandiseId: `gid://shopify/ProductVariant/${id}`,
          quantity: 1
        }], { source: 'amc-productos-blog' });
        form.dispatchEvent(cartLinesEvent);

        try {
          const r = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            credentials: 'same-origin',
            body: JSON.stringify({ items: [{ id, quantity: 1 }] })
          });
          if (!r.ok) throw new Error('No se pudo añadir');
          cartLinesEvent.resolve();
          window.location.assign('/cart');
        } catch (e) {
          cartLinesEvent.reject(e);
          update();
          status.textContent = 'No se ha podido añadir. Abre la ficha del producto para comprar.';
        }
      });

      form.append(label, selector, quantity, button);
      body.insertBefore(price, status);
      body.insertBefore(form, status);
      body.appendChild(productLink(card, 'Ver producto y condiciones'));
      update();
    } catch (err) {
      status.textContent = 'Consulta el producto y sus opciones de compra';
      body.appendChild(productLink(card, 'Ver ficha'));
    }
  };

  const init = () => {
    document.querySelectorAll('.amc-post .amc-card[data-product-handle]').forEach(initCard);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
